// client/src/components/IssueTriage/BugReportWidget.tsx
import { lazy, Suspense, useState, useEffect, useRef, useCallback } from 'react';
import type { DragEvent } from 'react';
import { X, Send, Loader2, Check, AlertCircle, ImagePlus } from 'lucide-react';
import { AITextArea } from '../shared/AITextArea/AITextArea';
import { useSubmitBugReport } from '../../hooks/issueTriage/useSubmitBugReport';
import { callableFailureMessage } from '../../lib/telemetry/namedCallable';
import { ElementPicker } from './ElementPicker';
import { ReportContextTray, type ReportAttachment } from './ReportContextTray';
import { useBackgroundScreenCapture } from './useBackgroundScreenCapture';
import { validateImageFile, fileToCompressedDataUrl, imageFilesFrom } from '../../lib/issueTriage/imageAttachment';
import { deriveReportTitle } from '../../lib/issueTriage/reportTitle';
import { isSensitiveReportRoute } from '../../lib/issueTriage/sensitiveRoutes';
import type { SelectedElement, IssueType, IssueSeverity } from '../../types/issue-triage';
import { ISSUE_TYPE_CONFIG, ISSUE_SEVERITY_CONFIG } from '../../types/issue-triage';

// The annotator is only needed once someone clicks an image — keep it out of
// the widget's own bundle so opening the widget stays instant.
const ImageAnnotator = lazy(() =>
  import('../shared/ImageAnnotator').then((m) => ({ default: m.ImageAnnotator }))
);

type Step = 'form' | 'picking' | 'submitting' | 'success' | 'error';

/** Most images a user can attach (the screen capture is separate). Mirrors the server. */
export const MAX_REPORT_ATTACHMENTS = 5;

/**
 * Keep the whole report under the callable's ~10 MB request limit, with room
 * for the captured context: the capture plus every attachment, as sent.
 */
export const MAX_REPORT_IMAGE_CHARS = 8 * 1024 * 1024;

interface BugReportWidgetProps {
  isOpen: boolean;
  onClose: () => void;
  prefillTitle?: string;
  prefillDescription?: string;
}

let attachmentSeq = 0;
const nextAttachmentId = () => `att-${++attachmentSeq}`;

/** One idempotency key per opened report (the server dedupes on it). */
function newRequestId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  return c?.randomUUID ? c.randomUUID() : `rq-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * BugReportWidget — "Report an Issue" (ISS-485).
 *
 * One step: a big description box, and context options that combine — the
 * automatic capture of this screen (on by default, taken in the background
 * after the widget opens), the user's own images, and "point to it". Any
 * image can be clicked to annotate; the edited version is what is sent.
 */
export function BugReportWidget({ isOpen, onClose, prefillTitle, prefillDescription }: BugReportWidgetProps) {
  const [step, setStep] = useState<Step>('form');
  const [description, setDescription] = useState('');
  const [issueType, setIssueType] = useState<IssueType>('bug');
  const [severity, setSeverity] = useState<IssueSeverity>('medium');
  const [captureEnabled, setCaptureEnabled] = useState(true);
  // Opened on an HR / payroll / PTO / recruiting page: capture defaults OFF.
  const [sensitivePage, setSensitivePage] = useState(false);
  // A lost response + retry must land on the same report, never a duplicate.
  const requestIdRef = useRef<string>(newRequestId());
  const [attachments, setAttachments] = useState<ReportAttachment[]>([]);
  const [selectedElement, setSelectedElement] = useState<SelectedElement | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [attachError, setAttachError] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [attachingCount, setAttachingCount] = useState(0);
  const modalRef = useRef<HTMLDivElement>(null);
  // A request started before the widget was reset must never repopulate a fresh report.
  const openGenRef = useRef(0);

  const { mutate: submitBugReport, reset: resetMutation } = useSubmitBugReport();
  const { capture, setCapture, status: captureStatus } = useBackgroundScreenCapture(isOpen && captureEnabled, modalRef);

  // Reset state when the widget opens.
  useEffect(() => {
    if (isOpen) {
      openGenRef.current++;
      requestIdRef.current = newRequestId();
      const sensitive = isSensitiveReportRoute(window.location.pathname);
      setSensitivePage(sensitive);
      setStep('form');
      setDescription(prefillDescription || '');
      setIssueType('bug');
      setSeverity('medium');
      setCaptureEnabled(!sensitive);
      setAttachments([]);
      setSelectedElement(null);
      setEditing(null);
      setErrorMessage('');
      setAttachError(null);
      setIsDraggingOver(false);
      setAttachingCount(0);
      resetMutation();
    }
  }, [isOpen, resetMutation, prefillDescription]);

  // Escape closes — but never mid-submit, mid-pick, or while annotating.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && step !== 'submitting' && step !== 'picking' && !editing) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, step, editing]);

  // Read inside state updaters, where closures over render-time values go
  // stale when two batches of images land back to back.
  const captureCharsRef = useRef(0);
  captureCharsRef.current = captureEnabled && capture ? capture.length : 0;

  const addImageFiles = useCallback(
    async (files: File[]) => {
      const gen = openGenRef.current;
      const accepted = files.slice(0, MAX_REPORT_ATTACHMENTS);
      const rejected = files.find((f) => !validateImageFile(f).ok);
      if (rejected) {
        const v = validateImageFile(rejected);
        setAttachError(v.ok ? null : v.reason);
        return;
      }
      setAttachingCount((n) => n + accepted.length);
      try {
        const urls = await Promise.all(accepted.map((f) => fileToCompressedDataUrl(f)));
        if (gen !== openGenRef.current) return; // widget reset meanwhile
        // Count and size are checked against the CURRENT list (prev), so two
        // batches landing back to back cannot jointly overshoot either limit.
        setAttachments((prev) => {
          const room = MAX_REPORT_ATTACHMENTS - prev.length;
          const added = urls.slice(0, Math.max(0, room)).map((dataUrl) => ({ id: nextAttachmentId(), dataUrl }));
          const used = captureCharsRef.current + prev.reduce((n, a) => n + a.dataUrl.length, 0);
          if (used + added.reduce((n, a) => n + a.dataUrl.length, 0) > MAX_REPORT_IMAGE_CHARS) {
            setAttachError('That would make the report too large to send. Remove an image or turn off the screen capture.');
            return prev;
          }
          setAttachError(
            added.length < files.length ? `You can attach up to ${MAX_REPORT_ATTACHMENTS} images; the rest were skipped.` : null
          );
          return [...prev, ...added];
        });
      } catch (err) {
        if (gen === openGenRef.current) {
          setAttachError(err instanceof Error ? err.message : 'Failed to process the image.');
        }
      } finally {
        if (gen === openGenRef.current) setAttachingCount((n) => Math.max(0, n - accepted.length));
      }
    },
    []
  );

  // Paste images anywhere while the form is showing. Text pastes pass through.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePaste = (e: ClipboardEvent) => {
      if (step !== 'form' || editing) return;
      const files = imageFilesFrom(e.clipboardData);
      if (files.length === 0) return;
      e.preventDefault();
      void addImageFiles(files);
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isOpen, step, editing, addImageFiles]);

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!e.dataTransfer.types.includes('Files')) return;
      // Always claim file drags: otherwise the browser navigates to the dropped file.
      e.preventDefault();
      if (step === 'form') setIsDraggingOver(true);
    },
    [step]
  );

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
    setIsDraggingOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDraggingOver(false);
      if (step !== 'form') return;
      const files = imageFilesFrom(e.dataTransfer);
      if (files.length > 0) {
        void addImageFiles(files);
      } else {
        setAttachError('Only image files can be dropped here. Attach a PNG, JPEG, GIF, or WebP.');
      }
    },
    [step, addImageFiles]
  );

  const handlePointToIt = useCallback(() => {
    if (modalRef.current) modalRef.current.style.display = 'none';
    setStep('picking');
  }, []);

  const restoreFromPicking = useCallback(() => {
    if (modalRef.current) modalRef.current.style.display = '';
    setStep('form');
  }, []);

  const handleElementSelected = useCallback(
    (element: SelectedElement) => {
      setSelectedElement(element);
      restoreFromPicking();
    },
    [restoreFromPicking]
  );

  const editingSrc = editing === 'capture' ? capture : attachments.find((a) => a.id === editing)?.dataUrl ?? null;

  const handleAnnotationSaved = useCallback(
    async (blob: Blob) => {
      const target = editing;
      setEditing(null);
      if (!target) return;
      try {
        const dataUrl = await fileToCompressedDataUrl(blob);
        if (target === 'capture') {
          setCapture(dataUrl);
        } else {
          setAttachments((prev) => prev.map((a) => (a.id === target ? { ...a, dataUrl } : a)));
        }
      } catch (err) {
        setAttachError(err instanceof Error ? err.message : 'Could not save the edited image.');
      }
    },
    [editing, setCapture]
  );

  const title = (prefillTitle?.trim() || deriveReportTitle(description)).slice(0, 200);
  // Sending mid-capture would silently drop the capture; wait for it (< ~1s).
  const capturing = captureEnabled && !capture && captureStatus === 'capturing';
  // The whole report must fit the callable's request limit — a large capture
  // can land AFTER photos were added, so this is checked at send time too.
  const totalImageChars =
    (captureEnabled && capture ? capture.length : 0) + attachments.reduce((n, a) => n + a.dataUrl.length, 0);
  const overBudget = totalImageChars > MAX_REPORT_IMAGE_CHARS;
  const canSend =
    description.trim().length > 0 && title.length > 0 && attachingCount === 0 && !capturing && !overBudget;

  const handleSubmit = useCallback(() => {
    if (!canSend) return;
    setStep('submitting');
    submitBugReport(
      {
        title,
        description: description.trim(),
        issueType,
        severity: issueType === 'bug' ? severity : undefined,
        screenshotDataUrl: captureEnabled && capture ? capture : undefined,
        attachmentDataUrls: attachments.length > 0 ? attachments.map((a) => a.dataUrl) : undefined,
        selectedElement: selectedElement || undefined,
        requestId: requestIdRef.current,
      },
      {
        onSuccess: (result) => {
          // A server that predates attachments ignores them and still says
          // "success" — the images would be lost silently. Say so instead.
          const sent = attachments.length;
          if (sent > 0 && result?.attachmentCount !== sent) {
            setErrorMessage(
              `Your report was saved${result?.reportId ? ` (${result.reportId})` : ''}, but ${sent === 1 ? 'its image was' : `its ${sent} images were`} not saved. Please send the images to the team another way.`
            );
            setStep('error');
            return;
          }
          setStep('success');
        },
        onError: (error) => {
          // callableFailureMessage, not `error.message`: the submit callable is
          // wrapped by invokeNamedCallable, which moves the server's own text
          // ("Title is required", ...) to `serverMessage`.
          setErrorMessage(callableFailureMessage(error) || 'Failed to submit bug report. Please try again.');
          setStep('error');
        },
      }
    );
  }, [canSend, title, description, issueType, severity, captureEnabled, capture, attachments, selectedElement, submitBugReport]);

  if (!isOpen) return null;

  return (
    <>
      <ElementPicker isActive={step === 'picking'} onSelect={handleElementSelected} onCancel={restoreFromPicking} />

      {editing && editingSrc && (
        <Suspense fallback={null}>
          <ImageAnnotator
            imageUrl={editingSrc}
            fileName={editing === 'capture' ? 'screen-capture.jpg' : 'attachment.jpg'}
            onSave={(blob) => void handleAnnotationSaved(blob)}
            onCancel={() => setEditing(null)}
          />
        </Suspense>
      )}

      <div
        ref={modalRef}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4 overflow-hidden"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div
          className="relative w-full max-w-xl max-h-[100vh] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden rounded-t-2xl sm:rounded-2xl bg-white shadow-xl box-border"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bug-report-title"
        >
          {isDraggingOver && step === 'form' && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-t-2xl sm:rounded-2xl border-2 border-dashed border-[#3FA5FF] bg-blue-50/90">
              <div className="flex flex-col items-center gap-2 text-[#007ACC]">
                <ImagePlus className="h-10 w-10" />
                <span className="text-sm font-medium">Drop images to attach</span>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between border-b border-slate-200 px-4 sm:px-6 py-3">
            <h2 id="bug-report-title" className="text-lg font-semibold text-slate-900">
              Report an issue
            </h2>
            {step !== 'submitting' && (
              <button onClick={onClose} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          <div className="px-4 sm:px-6 py-4">
            {(step === 'form' || step === 'picking') && (
              <div className="space-y-4">
                <AITextArea
                  label="What's going on?"
                  value={description}
                  onValueChange={(val) => setDescription(val.slice(0, 5000))}
                  placeholder={
                    issueType === 'bug'
                      ? 'What happened, and what did you expect instead? The first sentence becomes the title.'
                      : issueType === 'suggestion'
                        ? 'What would you like to see improved or added?'
                        : 'What are you trying to understand or accomplish?'
                  }
                  rows={6}
                  maxLength={5000}
                  autoFocus
                  enablePolish
                  enableVoice
                  polishTone="technical"
                  polishPrompt="Rewrite this as a clear, actionable report for a software developer: what happened, what was expected, steps to reproduce if given. Keep the first sentence a short summary. Be concise and specific."
                  fieldContext={`${ISSUE_TYPE_CONFIG[issueType].label} report for software development team`}
                  helperText={`${description.length}/5000 characters`}
                />

                <ReportContextTray
                  captureOffReason={sensitivePage && !captureEnabled ? 'Screen capture is off by default on this page, because it can show pay, time-off or personnel details. Turn it on only if the issue needs it.' : undefined}
                  captureEnabled={captureEnabled}
                  onToggleCapture={setCaptureEnabled}
                  capture={capture}
                  captureStatus={captureStatus}
                  attachments={attachments}
                  canAddMore={attachments.length < MAX_REPORT_ATTACHMENTS}
                  isAttaching={attachingCount > 0}
                  onAddFiles={(files) => void addImageFiles(files)}
                  onRemoveAttachment={(id) => setAttachments((prev) => prev.filter((a) => a.id !== id))}
                  onEdit={setEditing}
                  selectedElement={selectedElement}
                  onPointToIt={handlePointToIt}
                  onClearElement={() => setSelectedElement(null)}
                />

                {overBudget && (
                  <div className="flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-800 ring-1 ring-amber-200" role="status">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>The images are too large to send together. Remove an image or turn off the screen capture.</span>
                  </div>
                )}

                {attachError && (
                  <div className="flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-800 ring-1 ring-red-200" role="alert">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{attachError}</span>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {(Object.keys(ISSUE_TYPE_CONFIG) as IssueType[]).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setIssueType(type)}
                      aria-pressed={issueType === type}
                      className={`rounded-xl px-3 py-1.5 ring-1 ${issueType === type ? 'bg-[#3FA5FF]/10 ring-[#3FA5FF]/40 text-[#007ACC] font-medium' : 'ring-slate-200 text-slate-600 hover:bg-slate-50'}`}
                    >
                      {ISSUE_TYPE_CONFIG[type].emoji} {ISSUE_TYPE_CONFIG[type].label}
                    </button>
                  ))}
                  {issueType === 'bug' && (
                    <select
                      value={severity}
                      onChange={(e) => setSeverity(e.target.value as IssueSeverity)}
                      aria-label="Severity"
                      className="rounded-xl bg-white px-2 py-1.5 text-sm ring-1 ring-slate-200"
                    >
                      {(Object.keys(ISSUE_SEVERITY_CONFIG) as IssueSeverity[]).map((sev) => (
                        <option key={sev} value={sev}>
                          {ISSUE_SEVERITY_CONFIG[sev].emoji} {ISSUE_SEVERITY_CONFIG[sev].label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            )}

            {step === 'submitting' && (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="h-12 w-12 animate-spin text-[#007ACC]" />
                <p className="mt-4 text-sm text-slate-600">Sending your report…</p>
              </div>
            )}

            {step === 'success' && (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <Check className="h-6 w-6 text-green-600" />
                </div>
                <p className="mt-4 text-lg font-medium text-slate-900">Report sent</p>
                <p className="mt-2 text-sm text-slate-600">Thank you for helping us improve. We'll look into it.</p>
              </div>
            )}

            {step === 'error' && (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <p className="mt-4 text-lg font-medium text-slate-900">Submission Failed</p>
                <p className="mt-2 text-center text-sm text-slate-600">{errorMessage}</p>
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 px-4 sm:px-6 py-3">
            {step === 'form' && (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-slate-500">Technical details are included automatically.</p>
                <div className="flex gap-2 sm:justify-end">
                  <button onClick={onClose} className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!canSend}
                    className="flex items-center justify-center gap-2 rounded-xl bg-[#007ACC] px-4 py-2 text-sm font-medium text-white hover:bg-[#005fa3] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {attachingCount > 0 || capturing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {capturing ? 'Capturing screen…' : 'Send report'}
                  </button>
                </div>
              </div>
            )}
            {step === 'success' && (
              <div className="flex justify-center">
                <button onClick={onClose} className="rounded-xl bg-[#007ACC] px-6 py-2 text-sm font-medium text-white hover:bg-[#005fa3]">
                  Close
                </button>
              </div>
            )}
            {step === 'error' && (
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
                <button onClick={onClose} className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setStep('form');
                    setErrorMessage('');
                  }}
                  className="rounded-xl bg-[#007ACC] px-4 py-2 text-sm font-medium text-white hover:bg-[#005fa3]"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
